import { useEffect, useRef } from 'react';

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
let scriptLoadPromise = null;

function loadTurnstileScript() {
  if (window.turnstile) return Promise.resolve();
  if (!scriptLoadPromise) {
    scriptLoadPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Turnstile script.'));
      document.head.appendChild(script);
    });
  }
  return scriptLoadPromise;
}

/**
 * Renders a real Cloudflare Turnstile widget into the returned ref's element
 * -- but only when `siteKey` is actually configured. With no site key (every
 * environment's default today, local dev included), this is a complete
 * no-op: no script tag is injected, no network request fires, and behavior
 * is byte-for-byte unchanged from before this hook existed. Calls
 * `onVerify(token)` when the widget completes, and resets the caller's token
 * (via `onVerify('')`) on expiry/error so a stale token can't be submitted.
 */
export function useTurnstile(siteKey, onVerify) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onVerifyRef = useRef(onVerify);
  onVerifyRef.current = onVerify;

  useEffect(() => {
    if (!siteKey || !containerRef.current) return undefined;
    let cancelled = false;

    loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          callback: (token) => onVerifyRef.current?.(token),
          'expired-callback': () => onVerifyRef.current?.(''),
          'error-callback': () => onVerifyRef.current?.(''),
        });
      })
      .catch((err) => console.error('[turnstile]', err));

    return () => {
      cancelled = true;
      if (widgetIdRef.current !== null && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [siteKey]);

  return containerRef;
}
